import { useCallback } from "react";

import { API_CACHE_KEYS } from "api/cacheKeys";
import { useCustomMutation } from "api/useCustomMutation";
import { useCustomQuery } from "api/useCustomQuery";
import { DetectorGroup } from "components/createDetector/CreateDetectorModal";
import { JANZU_URL } from "config/config";
import { ListResponse } from "types/api";

import { BaseQueryOptions, CursorPaginationParams } from "../types";

export type DetectorGroupsParams = CursorPaginationParams & {
  ordering?: string;
  name?: string;
  search?: string;
};

type UseGetDetectorGroupsParams = {
  params?: DetectorGroupsParams;
  options?: BaseQueryOptions<ListResponse<DetectorGroup>>;
};

// Helper function to build URL with parameters
export function buildDetectorGroupsUrl(params?: DetectorGroupsParams): string {
  const url = new URL(`${JANZU_URL}reef-api/detector-groups`);
  const searchParams = new URLSearchParams();

  if (params?.paginate_from !== undefined) {
    searchParams.append("paginate_from", params.paginate_from.toString());
  }

  if (params?.page_size !== undefined) {
    searchParams.append("page_size", params.page_size.toString());
  }

  if (params?.ordering !== undefined) {
    searchParams.append("ordering", params.ordering);
  }

  if (params?.name !== undefined) {
    searchParams.append("name", params.name);
  }

  if (params?.search !== undefined) {
    searchParams.append("search", params.search);
  }

  url.search = searchParams.toString();
  return url.toString();
}

const useGetDetectorGroups = ({ params, options }: UseGetDetectorGroupsParams = {}) =>
  useCustomQuery<ListResponse<DetectorGroup>>(
    API_CACHE_KEYS.detectorGroups.list(params),
    buildDetectorGroupsUrl(params),
    options
  );

const useCreateDetectorGroup = () =>
  useCustomMutation<DetectorGroup, CreateDetectorGroupData>(`${JANZU_URL}reef-api/detector-groups`, "POST", {
    cacheKeysToInvalidate: [API_CACHE_KEYS.detectorGroups.all],
  });

const useDeleteDetectorGroup = () =>
  useCustomMutation((id: string) => `${JANZU_URL}reef-api/detector-groups/${id}`, "DELETE", {
    cacheKeysToInvalidate: [API_CACHE_KEYS.detectorGroups.all],
  });

type UpdateDetectorGroupRequestParams = {
  id: string;
  name: string;
};

const useUpdateDetectorGroup = () =>
  useCustomMutation(
    ({ id }: UpdateDetectorGroupRequestParams) => `${JANZU_URL}reef-api/detector-groups/${id}`,
    "PATCH",
    { cacheKeysToInvalidate: [API_CACHE_KEYS.detectorGroups.all] }
  );

type UseDetectorGroupsParams = {
  getOptions?: {
    params?: DetectorGroupsParams;
    options?: BaseQueryOptions<ListResponse<DetectorGroup>>;
  };
};

export type CreateDetectorGroupData = {
  name: string;
};

export const useDetectorGroups = ({ getOptions }: UseDetectorGroupsParams = {}) => {
  const detectorGroups = useGetDetectorGroups({
    params: getOptions?.params,
    options: getOptions?.options,
  });
  const createDetectorGroup = useCreateDetectorGroup();
  const updateDetectorGroup = useUpdateDetectorGroup();
  const deleteDetectorGroup = useDeleteDetectorGroup();

  // Manual fetch function for dynamic scenarios
  const fetchDetectorGroups = useCallback(
    async (params: DetectorGroupsParams): Promise<ListResponse<DetectorGroup>> => {
      const url = buildDetectorGroupsUrl(params);
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch detector groups: ${response.statusText}`);
      }
      return response.json();
    },
    []
  );

  return {
    // Get operations
    detectorGroups: detectorGroups.data,
    isLoadingDetectorGroups: detectorGroups.isFetching,
    detectorGroupsError: detectorGroups.error,
    refetchDetectorGroups: detectorGroups.refetch,
    fetchDetectorGroups,

    // Create operation
    dataCreateDetectorGroup: createDetectorGroup.data,
    createDetectorGroup: createDetectorGroup.mutate,
    createDetectorGroupAsync: createDetectorGroup.mutateAsync,
    isCreatingDetectorGroup: createDetectorGroup.isLoading,
    createDetectorGroupError: createDetectorGroup.error,

    // Update operations
    updateDetectorGroup: updateDetectorGroup.mutate,
    updateDetectorGroupAsync: updateDetectorGroup.mutateAsync,
    isUpdatingDetectorGroup: updateDetectorGroup.isLoading,
    updateDetectorGroupError: updateDetectorGroup.error,

    // Delete operations
    deleteDetectorGroup: deleteDetectorGroup.mutate,
    deleteDetectorGroupAsync: deleteDetectorGroup.mutateAsync,
    isDeletingDetectorGroup: deleteDetectorGroup.isLoading,
    deleteDetectorGroupError: deleteDetectorGroup.error,

    // Combined loading state
    isLoading:
      detectorGroups.isFetching ||
      createDetectorGroup.isLoading ||
      updateDetectorGroup.isLoading ||
      deleteDetectorGroup.isLoading,
  };
};
